import _ from "lodash";
import { defineStore } from "pinia";

import { contentCrud } from "../api/content";
import { contentFileVersionCrud } from "../api/contentFileVersion";
import {
  Content,
  CreateContentLocal,
  CreateContentStatus,
  ImportBatchItemDto,
  ImportBatchItemResultDto,
  UploadFileStatus,
} from "../api/models/content";
import { uploadFile } from "../api/upload";
import {
  generateFileFromBlob,
  hasSourceRelativePathFolder,
  isSourceRelativePathValid,
  normalizeSourceRelativePath,
  shouldPreserveFolderStructure,
} from "../components/uploadContent/utils/helpers";
import { getPayload } from "../utils";
import { AppError, ContentError } from "../utils/error";
import { ResumableFileUploader } from "../utils/resumable_file_uploader";

import { useUserStore } from "./user";

export const UPLOAD_CONCURRENCY_LIMIT = 5;

class UploadConcurrencyLimiter {
  private activeJobsQuantity = 0;
  private waitingJobs: (() => void)[] = [];

  async acquire(): Promise<void> {
    if (this.activeJobsQuantity < UPLOAD_CONCURRENCY_LIMIT) {
      this.activeJobsQuantity += 1;

      return;
    }

    await new Promise<void>((resolve) => this.waitingJobs.push(resolve));
    this.activeJobsQuantity += 1;
  }

  release(): void {
    this.activeJobsQuantity = Math.max(0, this.activeJobsQuantity - 1);
    const nextJob = this.waitingJobs.shift();
    nextJob?.();
  }
}

const dragAndDropUploadLimiter = new UploadConcurrencyLimiter();

function getFileNameWithExtension(content: CreateContentLocal): string | null {
  if (content.fileUrl instanceof File && content.fileUrl.name.trim().length > 0) {
    return content.fileUrl.name;
  }

  const fileName = content.fileName?.trim();
  const fileExtension = content.fileExtension?.trim().replace(/^\.+/, "");

  if (!fileName || !fileExtension) {
    return null;
  }

  return `${fileName}.${fileExtension.toLowerCase()}`;
}

function shouldPreserveContentFolderStructure(content: CreateContentLocal): boolean {
  return shouldPreserveFolderStructure(content, { isMultipleUploadContext: true });
}

function getNormalizedSourceRelativePathForImport(content: CreateContentLocal): string {
  const normalizedSourceRelativePath = normalizeSourceRelativePath(
    content.sourceRelativePath ?? ""
  );

  if (hasSourceRelativePathFolder(normalizedSourceRelativePath)) {
    if (!isSourceRelativePathValid(normalizedSourceRelativePath)) {
      throw new ContentError(
        content.id,
        "create",
        "Invalid folder path. Check the file path and try again."
      );
    }

    return normalizedSourceRelativePath;
  }

  const sourceRelativePath =
    normalizedSourceRelativePath.length > 0
      ? normalizedSourceRelativePath
      : getFileNameWithExtension(content);

  if (!sourceRelativePath) {
    throw new ContentError(
      content.id,
      "create",
      "Could not determine the file name for this upload. Please select the file again."
    );
  }

  return sourceRelativePath;
}

export const useUploadContentStore = defineStore({
  id: "uploadContent",
  state: () => {
    return {
      isLoading: false,
      showIconOnUploadButton: false,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      contents: <CreateContentLocal[]>undefined!,
    };
  },
  actions: {
    tryUploadContentAgain(contentId: string, version: number): void {
      const content = this.getByIdAndVersionOrFail(contentId, version);
      if (
        shouldPreserveContentFolderStructure(content) &&
        content.uploadFileStatus === UploadFileStatus.UPLOADED
      ) {
        if (content.createContentStatus !== CreateContentStatus.CREATED) {
          this.importDragAndDropBatch([content]);
        }

        return;
      }

      this.createContent(content);
    },
    hideUploadButtonIcon(): void {
      this.showIconOnUploadButton = false;
    },
    async loadRecentUploads() {
      const userStore = useUserStore();
      if (!this.contents && userStore.isActiveCompanyUser) {
        this.contents = await contentCrud.getUserRecentUploads();
        this.contents.forEach((content) => {
          content.uploadFileStatus = UploadFileStatus.UPLOADED;
          content.createContentStatus = CreateContentStatus.CREATED;
        });
      }

      return this.contents;
    },
    async upload(
      content: CreateContentLocal,
      options: { isNewVersion?: boolean; isCreate: boolean }
    ) {
      this.showIconOnUploadButton = true;

      this.upsert({ ...content, updatedDate: new Date() });
      this.setCreatingStatus(content.id, content.version);

      try {
        if (
          ![UploadFileStatus.UPLOADED, UploadFileStatus.UPLOADING].includes(
            content.uploadFileStatus
          )
        ) {
          if (content.coverImageLocalSrcUrl && !content.coverImageUrl) {
            await this.uploadCoverImage(content.id, content.version);
          }

          await this.uploadStorageFile(content.id, content.fileUrl as File, content.version);
        }
        await this.createOrUpdate(content, options);
      } catch (error) {
        if (error instanceof ContentError) {
          const { contentId, type, message } = error;

          const _content = this.getByIdAndVersionOrFail(contentId);
          _content.errorMessage = message;

          if (type === "upload") {
            _content.uploadFileStatus = UploadFileStatus.ERROR;
          }
          _content.createContentStatus = CreateContentStatus.ERROR;
        }
      } finally {
        this.showIconOnUploadButton = true;
      }
    },
    async updateContentVersion(content: CreateContentLocal, isNewVersion: boolean): Promise<void> {
      const newContent = _.cloneDeep(content);

      await this.upload(newContent, { isCreate: false, isNewVersion });
    },
    async createContent(content: CreateContentLocal): Promise<void> {
      if (shouldPreserveContentFolderStructure(content)) {
        await this.uploadDragAndDropContents([content]);

        return;
      }

      await this.upload(content, { isCreate: true });
    },
    async createContents(contents: CreateContentLocal[]): Promise<void> {
      const fileInputContents = contents.filter(
        (content) => !shouldPreserveContentFolderStructure(content)
      );
      const dragAndDropContents = contents.filter((content) =>
        shouldPreserveContentFolderStructure(content)
      );

      fileInputContents.forEach((content, index) => {
        setTimeout(async () => {
          await this.createContent(_.cloneDeep(content));
        }, 1000 * index);
      });

      if (dragAndDropContents.length > 0) {
        await this.uploadDragAndDropContents(
          dragAndDropContents.map((content) => _.cloneDeep(content))
        );
      }
    },
    async uploadDragAndDropContents(contents: CreateContentLocal[]): Promise<void> {
      this.showIconOnUploadButton = true;
      contents.forEach((content) => this.upsert({ ...content, updatedDate: new Date() }));

      const invalidContent = contents.find(
        (content) =>
          shouldPreserveContentFolderStructure(content) &&
          !isSourceRelativePathValid(content.sourceRelativePath ?? "")
      );

      if (invalidContent) {
        invalidContent.createContentStatus = CreateContentStatus.ERROR;
        invalidContent.errorMessage = "Invalid folder path. Check the file path and try again.";

        return;
      }

      const uploadedContents = await Promise.all(
        contents.map(async (content) => {
          const storedContent = this.getByIdAndVersionOrFail(content.id, content.version);
          storedContent.sourceRelativePath =
            getNormalizedSourceRelativePathForImport(storedContent);

          if (storedContent.uploadFileStatus !== UploadFileStatus.UPLOADED) {
            if (storedContent.fileUrl instanceof File) {
              await this.uploadDragAndDropStorageFile(
                storedContent.id,
                storedContent.fileUrl,
                storedContent.version
              );
            }
          }

          return this.getByIdAndVersionOrFail(content.id, content.version);
        })
      );

      const contentsReadyToImport = uploadedContents.filter(
        (content) => content.uploadFileStatus === UploadFileStatus.UPLOADED
      );

      contentsReadyToImport.forEach((content) =>
        this.setCreatingStatus(content.id, content.version)
      );
      await this.importDragAndDropBatch(contentsReadyToImport);
    },
    deleteContentsByIds(contentIds: string[]): void {
      const remainingContent = this.contents.filter(({ id }) => !contentIds.includes(id));

      this.contents.splice(0, this.contents.length, ...remainingContent);
    },
    addOrUpdateLocalContent(content: CreateContentLocal): void {
      this.upsert(content);
      this.showIconOnUploadButton = true;
    },
    cancelUpload(content: CreateContentLocal): void {
      if (content.uploadFileStatus !== UploadFileStatus.UPLOADING || !content.cancelUpload) {
        return;
      }
      content.cancelUpload();
      this.updateUploadProgress(content.id, content.version, undefined);
    },
    async uploadDragAndDropStorageFile(
      contentId: string,
      localFile: File,
      version = 1
    ): Promise<void> {
      await dragAndDropUploadLimiter.acquire();
      try {
        const content = this.getByIdAndVersionOrFail(contentId, version);
        if (content.coverImageLocalSrcUrl && !content.coverImageUrl) {
          await this.uploadCoverImage(contentId, version);
        }

        await this.uploadStorageFile(contentId, localFile, version);
      } finally {
        dragAndDropUploadLimiter.release();
      }
    },
    async uploadCoverImage(contentId: string, version = 1): Promise<void> {
      const content = this.getByIdAndVersionOrFail(contentId, version);
      if (!content.coverImageLocalSrcUrl || content.coverImageUrl) {
        return;
      }

      const coverFile = await generateFileFromBlob(content.coverImageLocalSrcUrl);
      const { fileName } = await uploadFile(coverFile);
      const storedContent = this.getByIdAndVersionOrFail(contentId, version);

      storedContent.coverImageUrl = fileName;
    },
    async uploadStorageFile(contentId: string, localFile: File, version = 1): Promise<void> {
      if (!localFile) {
        throw new ContentError(
          contentId,
          "create",
          "Content file not found, please try selecting it again"
        );
      }
      try {
        const uploader = new ResumableFileUploader(localFile, {
          onUploadProgress: (progress) => this.updateUploadProgress(contentId, version, progress),
        });
        const content = this.getByIdAndVersionOrFail(contentId, version);

        content.cancelUpload = uploader.abort.bind(uploader);
        this.setUploadingStatus(contentId, version);

        const { fileName } = await uploader.upload();
        this.setUploadSuccessStatus(contentId, version, fileName);
      } catch (error) {
        throw new ContentError(contentId, "upload", error);
      }
    },
    async createOrUpdate(
      contentData: CreateContentLocal,
      { isNewVersion, isCreate }: { isNewVersion?: boolean; isCreate: boolean }
    ) {
      const content = this.getByIdAndVersionOrFail(contentData.id, contentData.version);

      if (content.uploadFileStatus !== UploadFileStatus.UPLOADED) {
        throw new ContentError(contentData.id, "create");
      }
      try {
        this.setCreatingStatus(contentData.id, contentData.version);

        let response: Content;

        if (isCreate) {
          const payload = getPayload(content, "CREATE");
          response = await contentCrud.create(payload);
        } else {
          response = await contentFileVersionCrud.updateContentFile(contentData.id, {
            fileUrl: <any>content.fileUrl,
            isNewVersion: !!isNewVersion,
          });
        }
        this.setCreationSuccessStatus(contentData.id, contentData.version, response);
      } catch (error) {
        throw new ContentError(contentData.id, "create", error);
      }
    },

    buildImportBatchItem(content: CreateContentLocal): ImportBatchItemDto {
      const payload = getPayload(content, "CREATE");
      const normalizedSourceRelativePath = getNormalizedSourceRelativePathForImport(content);

      return {
        title: payload.title,
        description: payload.description,
        languageId: payload.languageId,
        isConfidential: payload.isConfidential,
        isRecommended: payload.isRecommended,
        expirationScheduling: payload.expirationScheduling,
        fileUrl: String(payload.fileUrl),
        coverImageUrl: payload.coverImageUrl,
        foldersIds: content.originalFoldersIds ?? payload.foldersIds,
        sourceRelativePath: normalizedSourceRelativePath,
      };
    },
    async importDragAndDropBatch(contents: CreateContentLocal[]): Promise<void> {
      if (contents.length === 0) {
        return;
      }

      contents.forEach((content) => this.setCreatingStatus(content.id, content.version));

      try {
        const response = await contentCrud.importBatch({
          items: contents.map((content) => this.buildImportBatchItem(content)),
        });

        response.items.forEach((result) => {
          const content = contents[result.index];
          if (content) {
            this.applyImportBatchResult(content, result);
          }
        });
      } catch (error) {
        const errorResults = this.getImportBatchErrorResults(error);

        contents.forEach((content, index) => {
          const storedContent = this.getByIdAndVersionOrFail(content.id, content.version);
          const result = errorResults.find((item) => item.index === index);

          storedContent.createContentStatus = CreateContentStatus.ERROR;
          storedContent.errorMessage =
            result?.error?.message ??
            "Could not create this content. Check your connection and try again.";
        });
      }
    },
    getImportBatchErrorResults(error: unknown): ImportBatchItemResultDto[] {
      const errorWithResponse = error as { response?: { items?: ImportBatchItemResultDto[] } };

      const items = errorWithResponse.response?.items;

      if (!Array.isArray(items)) {
        return [];
      }

      return items;
    },
    applyImportBatchResult(content: CreateContentLocal, result: ImportBatchItemResultDto): void {
      const storedContent = this.getByIdAndVersionOrFail(content.id, content.version);
      const { originalFoldersIds } = storedContent;

      if (result.status === "CREATED" && result.id) {
        this.setCreationSuccessStatus(storedContent.id, storedContent.version, {
          ...(storedContent as unknown as Content),
          id: result.id,
          title: result.title ?? storedContent.title,
          folders: undefined,
          foldersIds: result.foldersIds ?? storedContent.foldersIds,
          originalFoldersIds,
        } as unknown as Content);

        return;
      }

      storedContent.createContentStatus = CreateContentStatus.ERROR;
      storedContent.errorMessage =
        result.error?.message ?? "Could not create this content. Please try again.";
    },

    getByIdAndVersionOrFail(contentId: string, version = 1): CreateContentLocal {
      const content = this.contents.find(
        ({ id, version: contentVersion }) => id === contentId && contentVersion === version
      );

      if (!content) {
        throw new AppError("Content not found");
      }

      return content;
    },

    getIndexByIdAndVersion(contentId: string, version = 1): number {
      return this.contents.findIndex(
        ({ id, version: contentVersion }) => id === contentId && contentVersion === version
      );
    },

    upsert(content: CreateContentLocal): void {
      const contentIndex = this.getIndexByIdAndVersion(content.id, content.version);

      if (contentIndex >= 0) {
        Object.assign(this.contents[contentIndex], content);
      } else {
        this.contents.unshift(content);
      }

      this.showIconOnUploadButton = true;
    },

    delete(contentId: string, version = 1): void {
      const contentIndex = this.getIndexByIdAndVersion(contentId, version);

      if (contentIndex >= 0) {
        this.contents.splice(contentIndex, 1);
      }
    },

    setCreatingStatus(contentId: string, version = 1): void {
      const content = this.getByIdAndVersionOrFail(contentId, version);
      content.createContentStatus = CreateContentStatus.CREATING;
    },

    setUploadingStatus(contentId: string, version = 1): void {
      const content = this.getByIdAndVersionOrFail(contentId, version);
      content.uploadFileStatus = UploadFileStatus.UPLOADING;
    },

    setUploadSuccessStatus(contentId: string, version: number, fileName: string): void {
      const content = this.getByIdAndVersionOrFail(contentId, version);

      content.fileUrl = fileName;
      content.errorMessage = null;
      content.uploadFileStatus = UploadFileStatus.UPLOADED;
    },

    updateUploadProgress(contentId: string, version: number, progress: number | undefined): void {
      const content = this.getByIdAndVersionOrFail(contentId, version);
      content.progress = progress;
    },

    setCreationSuccessStatus(contentId: string, version: number, createdContent: Content): void {
      const content = this.getByIdAndVersionOrFail(contentId, version);

      Object.assign(content, {
        ...createdContent,
        createContentStatus: CreateContentStatus.CREATED,
        errorMessage: null,
        coverImageLocalSrcUrl: null,
      });
    },
  },
});
